/**
 * Единственная дверь к `Intl`.
 *
 * ─── Зачем обёртка ──────────────────────────────────────────────────────────
 *
 * До B1 в репозитории было девятнадцать вызовов `new Intl.*` и в каждом стояли
 * буквами `'ru-RU'` и `'Europe/Moscow'`. Пока рынок один, это верно; на втором
 * каждая такая буква становится дефектом, который видит клиент: сутки замера,
 * посчитанные по Москве для того, кто живёт в Дубае, и цена в дирхамах,
 * напечатанная рублём.
 *
 * ─── Арабские цифры ─────────────────────────────────────────────────────────
 *
 * Второе, ради чего дверь одна. У `ar-AE` система счисления по умолчанию —
 * `arab`, и `Intl` напечатает индекс видимости как ٤٢. Дизайн-система (B3)
 * требует западных цифр во всех показаниях на всех рынках: показание клиент
 * сверяет с выгрузкой, со счётом и со снимком чужого кабинета. Принуждение
 * стоит в теге локали (`shared/src/i18n/locale.ts`, `-u-nu-latn`), а действует
 * оно только на тех, кто ходит сюда, — поэтому мимо этого файла `Intl` не
 * зовётся нигде.
 *
 * ─── Кеш ────────────────────────────────────────────────────────────────────
 *
 * `Intl`-объекты дороги в создании и дёшевы в использовании, а зовут их в
 * ячейках таблиц. Здесь они живут по ключу «тег + параметры»; при смене языка
 * ключ меняется сам, поэтому сбрасывать кеш не нужно.
 */

import { localeTag } from './i18n/runtime';
import { market } from './i18n/market';

const cache = new Map<string, unknown>();

function memo<T>(kind: string, options: object | undefined, make: () => T): T {
  const key = `${kind} ${localeTag()} ${options ? JSON.stringify(options) : ''}`;
  const found = cache.get(key);
  if (found) return found as T;
  const made = make();
  cache.set(key, made);
  return made;
}

/** Числа. Разряды, дроби и проценты — только отсюда. */
export function numberFormat(options?: Intl.NumberFormatOptions): Intl.NumberFormat {
  return memo('number', options, () => new Intl.NumberFormat(localeTag(), options));
}

/**
 * Даты и время. Пояс по умолчанию — рыночный: продукт считает сутки замера в
 * одном поясе, и подпись под графиком обязана называть те же сутки, что и
 * число над ним.
 */
export function dateTimeFormat(
  options?: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const full = { timeZone: market().timeZone, ...options };
  return memo('date', full, () => new Intl.DateTimeFormat(localeTag(), full));
}

/** Формы множественного числа. Правила берёт CLDR браузера, а не мы. */
export function pluralRules(options?: Intl.PluralRulesOptions): Intl.PluralRules {
  return memo('plural', options, () => new Intl.PluralRules(localeTag(), options));
}

/**
 * Сортировка слов.
 *
 * Отдельная функция, а не `localeCompare` по месту, по двум причинам. Первая:
 * `localeCompare(other, 'ru')` — это тот же зашитый рынок, только записанный
 * иначе, и на арабском он сортирует по чужому алфавиту. Вторая: сортировка
 * колонки — то, что человек читает как утверждение о данных, и она обязана
 * быть одинаковой во всех таблицах кабинета.
 *
 * Машинные строки — коды движков, даты `2026-08-08` — сюда не ходят: у них
 * порядок байтовый и от языка не зависит, а `Intl` на них только тратит время.
 */
export function collator(options?: Intl.CollatorOptions): Intl.Collator {
  return memo('collator', options, () => new Intl.Collator(localeTag(), options));
}

/**
 * Дата для сервера: `2026-08-08`. ISO 8601, контракт принимает и отдаёт только
 * такие.
 *
 * Единственная функция файла, которая **не** спрашивает активную локаль, и
 * поэтому она стоит именно здесь, а не «где-нибудь рядом с запросом»: дверь к
 * `Intl` одна, и исключение из общего правила обязано лежать внутри неё, где
 * его видно вместе с правилом. Вынесенное наружу, оно читается как обход.
 *
 * `en-CA` здесь — **формат, а не язык**: это единственная стандартная локаль,
 * чья числовая запись даты совпадает с ISO 8601. Активная локаль сюда не
 * подходит по определению — это запись для сервера, а не для человека: `ru-RU`
 * отправил бы в запрос `08.08.2026`, арабский — ещё и арабо-индийскими цифрами.
 *
 * Пояс, наоборот, рыночный: сутки замера считает продукт, и граница суток у РФ
 * и у глобала разная.
 */
export function isoDate(value: Date): string {
  const zone = market().timeZone;
  const key = `iso ${zone}`;
  let found = cache.get(key) as Intl.DateTimeFormat | undefined;
  if (!found) {
    found = new Intl.DateTimeFormat('en-CA', { timeZone: zone });
    cache.set(key, found);
  }
  return found.format(value);
}
